import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Col,
  Input,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import * as url from "../../../helpers/url_helper";

const baseSelectStyles = {
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: "white",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    fontWeight: "normal",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "#6c757d",
    fontWeight: "normal",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#333",
    fontWeight: "normal",
  }),
  control: (provided: any) => ({
    ...provided,
    minHeight: 32,
    height: 32,
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: 32,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: 12,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
  }),
};

type SaveFilterPresetListProps = {
  token: string | null;
  refreshKey?: number;
  onSelect?: (preset: any) => void;
  selectedPresetId?: string | null;
  onEdit?: (preset: any) => void;
};

const SaveFilterPresetList = ({
  token,
  refreshKey = 0,
  onSelect,
  selectedPresetId,
  onEdit,
}: SaveFilterPresetListProps) => {
  const [presets, setPresets] = useState<any[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [previewPreset, setPreviewPreset] = useState<any | null>(null);
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const searchTimeout = useRef<any>(null);

  const fetchPresets = async (searchVal: string = "") => {
    try {
      setPresetsLoading(true);
      const body = {
        draw: 1,
        start: 0,
        length: 100,
        search: {
          value: searchVal || "",
        },
      };
      const res = await axios.post(
        `${url.BASE_URL}${url.LIST_FILTER_PRESETS}`,
        body,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        }
      );
      setPresets(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching presets", err);
      toast.error("Failed to load presets");
    } finally {
      setPresetsLoading(false);
    }
  };

  useEffect(() => {
    fetchPresets("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Debounced search handler for API search
  const handleSearchInput = (inputValue: string, action: any) => {
    if (action.action === "input-change") {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      searchTimeout.current = setTimeout(() => {
        fetchPresets(inputValue || "");
      }, 400);
    }
  };

  // Convert presets to options for react-select
  const presetOptions = useMemo(() => {
    return presets.map((p: any) => ({
      value: p._id || p.id,
      label: p.name,
      preset: p,
    }));
  }, [presets]);

  // Format option label with black dot indicator for "For All" filters
  const formatOptionLabel = ({ label, preset }: any) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span>{label}</span>
        {preset?.isForAll && (
          <span
            style={{
              width: "5px",
              height: "5px",
              borderRadius: "50%",
              backgroundColor: "#5a5a5aff",
              marginLeft: "4px",
              flexShrink: 0,
            }}
          />
        )}
      </div>
    );
  };

  // Find selected option
  const selectedOption = useMemo(() => {
    if (!selectedPresetId) return null;
    return presetOptions.find((opt) => opt.value === selectedPresetId) || null;
  }, [selectedPresetId, presetOptions]);

  // Handle preset selection from dropdown - apply filter when selected
  const handlePresetChange = (option: any) => {
    if (option && option.preset && onSelect) {
      onSelect(option.preset);
    }
  };

  const handleDeletePreset = async () => {
    if (!previewPreset?._id) return;
    try {
      setDeleting(true);
      await axios.post(
        `${url.BASE_URL}${url.DELETE_FILTER_PRESETS}`,
        { filterId: previewPreset._id },
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        }
      );
      toast.success("Filter deleted");
      setPreviewOpen(false);
      fetchPresets();
    } catch (err) {
      console.error("Error deleting filter", err);
      toast.error("Failed to delete filter");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Row>
        <Col
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 8,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
             {selectedOption && selectedOption.preset && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                // padding: "3px",
                backgroundColor: "#f9fafb",
                borderRadius: "6px",
              }}
            >
              <span
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 4px",
                }}
                onClick={() => {
                  setPreviewPreset(selectedOption.preset);
                  setPreviewOpen(true);
                }}
                title="View details"
              >
                <box-icon name="show" size="18px" color="#454545"></box-icon>
              </span>
              <span
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 4px",
                }}
                onClick={() => {
                  onEdit?.(selectedOption.preset);
                }}
                title="Edit filter"
              >
                <box-icon name="pencil" size="18px" color="#454545"></box-icon>
              </span>
              <span
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 4px",
                }}
                onClick={() => {
                  setPreviewPreset(selectedOption.preset);
                  setDeleteConfirmOpen(true);
                }}
                title="Delete filter"
              >
                <box-icon name="trash" size="18px" color="#454545"></box-icon>
              </span>
            </div>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minWidth: 220,
            }}
          >
            <Select
              isSearchable
              options={presetOptions}
              value={selectedOption}
              onChange={handlePresetChange}
              onInputChange={handleSearchInput}
              isLoading={presetsLoading}
              placeholder="Select a filter to apply"
              menuPortalTarget={document.body}
              styles={baseSelectStyles}
              filterOption={() => true}
              formatOptionLabel={formatOptionLabel}
              onFocus={() => {
                if (!presetOptions.length) {
                  fetchPresets("");
                }
              }}
            />
          </div>
        </Col>
      </Row>

      <Modal
        isOpen={isPreviewOpen}
        toggle={() => setPreviewOpen(false)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setPreviewOpen(false)}>
          {previewPreset?.name || "Filter Details"}
        </ModalHeader>
        <ModalBody>
          {previewPreset ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <strong>Description:</strong> {previewPreset.description || "—"}
              </div>
              <div>
                <strong>Module:</strong> {previewPreset.moduleName || "user"}
              </div>
              <div>
                <strong>Active:</strong> {previewPreset.isActive ? "Yes" : "No"}{" "}
                | <strong>For All:</strong>{" "}
                {previewPreset.isForAll ? "Yes" : "No"}
              </div>
              <div>
                <strong>Filter Type:</strong>{" "}
                {previewPreset.filterPayload?.filterType ?? "—"}
              </div>
              <div>
                <strong>Filter:</strong>{" "}
                {previewPreset.filterPayload?.filter || "—"}
              </div>
              <div>
                <strong>City IDs:</strong>{" "}
                {previewPreset.filterPayload?.cityId?.length
                  ? previewPreset.filterPayload.cityId.join(", ")
                  : "—"}
              </div>
              {previewPreset.filterPayload?.activityFilter && (
                <div>
                  <strong>Activity Filter:</strong>{" "}
                  {JSON.stringify(previewPreset.filterPayload.activityFilter)}
                </div>
              )}
              {previewPreset.filterPayload?.deviceFilter && (
                <div>
                  <strong>Device Filter:</strong>{" "}
                  {JSON.stringify(previewPreset.filterPayload.deviceFilter)}
                </div>
              )}
              <div>
                <strong>Created:</strong>{" "}
                {previewPreset.createdAt
                  ? new Date(previewPreset.createdAt).toLocaleString()
                  : "—"}
              </div>
            </div>
          ) : (
            "No details"
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            size="sm"
            onClick={() => setPreviewOpen(false)}
            disabled={deleting}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={isDeleteConfirmOpen}
        toggle={() => setDeleteConfirmOpen(false)}
        centered
        size="sm"
      >
        <ModalHeader toggle={() => setDeleteConfirmOpen(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>Are you sure you want to delete this filter?</ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            size="sm"
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            size="sm"
            disabled={deleting}
            onClick={() => {
              handleDeletePreset();
              setDeleteConfirmOpen(false);
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SaveFilterPresetList;
